import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useProfile } from '@/hooks/useProfile';
import {
    User,
    Mail,
    Building,
    FileText,
    AlertTriangle,
    X,
    Save,
    Edit2,
    Calendar,
    Trash2
} from 'lucide-react';

const ProfilePage = () => {
    const {
        profile,
        loading,
        isEditing,
        editForm,
        showDeleteConfirm,
        handleEditToggle,
        updateProfile,
        deleteAccount,
        handleInputChange,
        formatDate,
        setShowDeleteConfirm
    } = useProfile();

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    // Get initials for avatar fallback
    const initials = profile.display_name
        ?.split(' ')
        .map(part => part[0])
        .join('')
        .toUpperCase() || profile.email[0].toUpperCase();

    return (
        <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold">Profile</h1>
                {!isEditing ? (
                    <Button
                        onClick={handleEditToggle}
                        className="flex items-center gap-2"
                    >
                        <Edit2 className="h-4 w-4" />
                        Edit Profile
                    </Button>
                ) : (
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={handleEditToggle}
                            className="flex items-center gap-2"
                        >
                            <X className="h-4 w-4" />
                            Cancel
                        </Button>
                        <Button
                            onClick={updateProfile}
                            className="flex items-center gap-2"
                        >
                            <Save className="h-4 w-4" />
                            Save Changes
                        </Button>
                    </div>
                )}
            </div>

            {/* Profile Information */}
            <Card className="bg-white">
                <CardHeader>
                    <div className="flex items-center gap-4">
                        <Avatar className="h-16 w-16">
                            <AvatarImage src={profile.avatar_url} alt={profile.display_name} />
                            <AvatarFallback className="text-lg">{initials}</AvatarFallback>
                        </Avatar>
                        <div>
                            <CardTitle className="text-xl font-semibold">
                                {profile.display_name || 'My Account'}
                            </CardTitle>
                            <p className="text-sm text-gray-500">{profile.email}</p>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="space-y-2">
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                            id="email"
                            value={profile.email}
                            disabled
                            className="bg-gray-50"
                        />
                        <p className="text-sm text-gray-500">
                            Email cannot be changed. Contact support if you need to update it.
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="display_name">Name</Label>
                        <Input
                            id="display_name"
                            name="display_name"
                            value={isEditing ? editForm.display_name : profile.display_name}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            className={!isEditing ? 'bg-gray-50' : ''}
                            placeholder="Enter your name"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="company">Company</Label>
                        <Input
                            id="company"
                            name="company"
                            value={isEditing ? editForm.company : profile.company}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            className={!isEditing ? 'bg-gray-50' : ''}
                            placeholder="Enter your company name"
                        />
                    </div>

                    <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-2 text-gray-600">
                            <FileText className="h-4 w-4" />
                            <span className="text-sm">Total Websites Analyzed:</span>
                            <span className="text-sm font-medium">{profile.total_websites_analyzed}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 mt-2">
                            <Calendar className="h-4 w-4" />
                            <span className="text-sm">Member Since:</span>
                            <span className="text-sm font-medium">{formatDate(profile.created_at)}</span>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Delete Account Section */}
            <Card className="bg-white border-red-100">
                <CardHeader className="border-b border-red-100">
                    <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <div className="p-2 rounded-full bg-red-100">
                            <Trash2 className="h-4 w-4 text-red-600" />
                        </div>
                        Delete Account
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                    <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                        <div className="flex gap-3">
                            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                            <div className="space-y-1">
                                <h4 className="font-medium text-red-900">Warning: This action cannot be undone</h4>
                                <p className="text-sm text-red-700">
                                    Deleting your account will:
                                </p>
                                <ul className="text-sm text-red-700 list-disc pl-5 space-y-1">
                                    <li>Permanently remove all your data and analysis history</li>
                                    <li>Cancel any remaining credits</li>
                                    <li>Delete all saved configurations</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end pt-2">
                        <Button
                            variant="ghost"
                            onClick={() => setShowDeleteConfirm(true)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 flex items-center gap-2"
                        >
                            <Trash2 className="h-4 w-4" />
                            Delete Account
                        </Button>
                    </div>

                    <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                        <AlertDialogContent className="max-w-md">
                            <AlertDialogHeader>
                                <AlertDialogTitle className="text-red-600 flex items-center gap-2">
                                    <AlertTriangle className="h-5 w-5" />
                                    Confirm Account Deletion
                                </AlertDialogTitle>
                                <AlertDialogDescription className="space-y-3">
                                    <p className="font-medium text-gray-900">
                                        Are you absolutely sure you want to delete your account?
                                    </p>
                                    <p className="text-gray-600">
                                        This action is permanent and cannot be undone. All of your data, 
                                        including {profile.total_websites_analyzed} analyzed websites and account history 
                                        since {formatDate(profile.created_at)}, will be permanently deleted.
                                    </p>
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="gap-2 sm:gap-0">
                                <AlertDialogCancel className="sm:mr-2">
                                    No, Keep My Account
                                </AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={deleteAccount}
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                >
                                    Yes, Delete My Account
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </CardContent>
            </Card>
        </div>
    );
};

export default ProfilePage;